import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavLink, useLocation } from "react-router-dom";
import {
  FolderKanban,
  Calendar,
  Layers,
  Building2,
  Users,
  UserCog,
  Briefcase,
  LayoutDashboard,
  LogOut,
  Package,
  FileText,
  CheckSquare,
  Radio,
  Signal,
  ClipboardList,
  Settings,
  BarChart3,
  Library,
  HammerIcon,
  Wrench,
  BookOpen,
  Cloud,
  Receipt,
  LayoutGrid,
  Truck,
  CalendarRange,
  Smartphone,
  Mail,
  Lock,
  Server,
} from "lucide-react";
import logoLivemotion from "@/assets/logo-livemotion-hub-square.svg";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useDevToolsStatus } from "@/hooks/useDevTools";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { EquipmentSidebarTags } from "@/components/equipment/EquipmentSidebarTags";

interface NavSubItem {
  title: string;
  href: string;
  icon: typeof FolderKanban;
  employeeOnly?: boolean;
  adminOnly?: boolean;
  /** Only shown where dev tools are enabled (dev-env, previews) */
  devToolsOnly?: boolean;
}

interface NavItem {
  title: string;
  icon: typeof FolderKanban;
  href?: string;
  items?: NavSubItem[];
  /** Rendered after `items` inside the expanded group */
  extraSubItems?: (props: { onNavigate: () => void }) => JSX.Element;
  employeeOnly?: boolean;
  adminOnly?: boolean;
}

const navigation: NavItem[] = [
  {
    title: "Dashboard",
    icon: LayoutDashboard,
    href: "/",
  },
  {
    title: "Projects",
    icon: FolderKanban,
    items: [
      { title: "List", href: "/projects", icon: FolderKanban },
      { title: "Calendar", href: "/projects/calendar", icon: Calendar },
      {
        title: "Project Groups",
        href: "/projects/groups",
        icon: Layers,
        employeeOnly: true,
      },
    ],
  },
  {
    title: "Companies",
    icon: Building2,
    href: "/companies",
    employeeOnly: true,
  },
  {
    title: "People",
    icon: Users,
    employeeOnly: true,
    items: [
      { title: "All", href: "/people", icon: Users },
      { title: "Employees", href: "/people/employees", icon: UserCog },
      { title: "Freelancers", href: "/people/freelancers", icon: Briefcase },
      {
        title: "Crew Planning",
        href: "/crew-planning",
        icon: ClipboardList,
      },
    ],
  },
  {
    title: "Equipment",
    icon: Package,
    employeeOnly: true,
    items: [
      {
        title: "Overview",
        href: "/equipment",
        icon: LayoutGrid,
      },
    ],
    extraSubItems: EquipmentSidebarTags,
  },
  {
    title: "Vehicles",
    icon: Truck,
    employeeOnly: true,
    items: [
      { title: "Overview", href: "/vehicles", icon: LayoutGrid },
      { title: "Planning", href: "/vehicles/planning", icon: CalendarRange },
    ],
  },
  {
    title: "Studios",
    icon: Radio,
    href: "/studios",
    employeeOnly: true,
  },
  {
    title: "Docs",
    icon: BookOpen,
    href: "/docs",
    employeeOnly: true,
  },
  {
    title: "Finance",
    icon: Receipt,
    href: "/finance",
    employeeOnly: true,
  },
  {
    title: "Meeting Notes",
    icon: FileText,
    employeeOnly: true,
    items: [
      { title: "All notes", href: "/meeting-notes", icon: FileText },
      {
        title: "Management",
        href: "/meeting-notes/management",
        icon: Lock,
        adminOnly: true,
      },
    ],
  },
  {
    title: "Tasks",
    icon: CheckSquare,
    href: "/tasks",
    employeeOnly: true,
  },
  {
    title: "Worktime & Vaccation",
    href: "/my-balance",
    icon: BarChart3,
    employeeOnly: true,
  },
  {
    title: "Tools",
    icon: HammerIcon,
    employeeOnly: true,
    items: [
      {
        title: "Cloud Media",
        icon: Cloud,
        href: "/cloud-media",
      },
      {
        title: "Cloud Computers",
        icon: Server,
        href: "/cloud-computers",
      },
      {
        title: "Network Coverage",
        icon: Signal,
        href: "/network-coverage",
      },
      {
        title: "SIM Cards",
        icon: Smartphone,
        href: "/sims",
      },
    ],
  },
];

const adminNavigation: NavItem[] = [
  {
    title: "Admin",
    icon: Settings,
    adminOnly: true,
    items: [
      {
        title: "Settings",
        href: "/admin/settings",
        icon: Settings,
        adminOnly: true,
      },
      {
        title: "Default Functions",
        href: "/admin/default-functions",
        icon: Wrench,
        adminOnly: true,
      },
      {
        title: "HR Settings",
        icon: Settings,
        href: "/settings",
        adminOnly: true,
      },
      {
        title: "HR Overtime View",
        href: "/hr",
        icon: ClipboardList,
        adminOnly: true,
      },
      {
        title: "Maintenance",
        href: "/admin/maintenance",
        icon: Wrench,
        adminOnly: true,
      },
      {
        title: "Quote Templates",
        href: "/admin/quote-templates",
        icon: Library,
        adminOnly: true,
      },
      {
        title: "Mail Catcher",
        href: "/admin/mail-catcher",
        icon: Mail,
        adminOnly: true,
        devToolsOnly: true,
      },
    ],
  },
];

/** Title of the navigation group whose sub-items contain `pathname`. */
function activeGroupTitle(pathname: string) {
  return navigation.find((item) =>
    item.items?.some(
      (subItem) =>
        pathname === subItem.href || pathname.startsWith(`${subItem.href}/`),
    ),
  )?.title;
}

export function AppSidebar() {
  const location = useLocation();
  const { user, isEmployee, role, signOut } = useAuth();
  const devToolsStatus = useDevToolsStatus();
  const { isMobile, setOpenMobile } = useSidebar();
  // Groups start closed; only the group containing the current page opens,
  // also when navigating into it from elsewhere.
  const activeGroup = activeGroupTitle(location.pathname);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    activeGroup ? { [activeGroup]: true } : {},
  );
  useEffect(() => {
    if (activeGroup) {
      setOpenGroups((prev) =>
        prev[activeGroup] ? prev : { ...prev, [activeGroup]: true },
      );
    }
  }, [activeGroup]);

  const closeMobileSidebar = useCallback(() => {
    if (isMobile) {
      setOpenMobile(false);
    }
  }, [isMobile, setOpenMobile]);

  const isActive = (href: string) => location.pathname === href;
  const isGroupActive = (items?: { href: string }[]) =>
    items?.some((item) => location.pathname.startsWith(item.href)) ?? false;

  const filteredNavigation = navigation.filter(
    (item) => !item.employeeOnly || isEmployee,
  );

  const userInitials = user?.email?.slice(0, 2).toUpperCase() || "U";

  return (
    <Sidebar className="border-r border-sidebar-border">
      <SidebarHeader className="border-b border-sidebar-border p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sidebar-accent overflow-hidden">
            <img
              src={logoLivemotion}
              alt="LiveMotion"
              className="h-9 w-9 object-cover"
            />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-sidebar-foreground">
              LM Hub
            </h1>
            <p className="text-xs text-muted-foreground">Project Management</p>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="scrollbar-thin">
        <SidebarGroup>
          <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground">
            Navigation
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredNavigation.map((item) => {
                if (item.items) {
                  const filteredItems = item.items.filter(
                    (subItem) =>
                      (!subItem.employeeOnly || isEmployee) &&
                      (!subItem.adminOnly || role === "admin"),
                  );

                  if (filteredItems.length === 0) return null;

                  return (
                    <Collapsible
                      key={item.title}
                      open={openGroups[item.title]}
                      onOpenChange={(open) =>
                        setOpenGroups((prev) => ({
                          ...prev,
                          [item.title]: open,
                        }))
                      }
                    >
                      <SidebarMenuItem>
                        <SidebarMenuButton
                          asChild
                          className={cn(
                            "w-full justify-between",
                            isGroupActive(filteredItems) &&
                              "bg-sidebar-accent text-sidebar-accent-foreground",
                          )}
                        >
                          <NavLink
                            to={filteredItems[0].href}
                            onClick={() => {
                              setOpenGroups((prev) => ({
                                ...prev,
                                [item.title]: true,
                              }));
                              closeMobileSidebar();
                            }}
                          >
                            <span className="flex items-center gap-2">
                              <item.icon className="h-4 w-4" />
                              {item.title}
                            </span>
                            <ChevronDown
                              className={cn(
                                "h-4 w-4 transition-transform duration-200",
                                openGroups[item.title] && "rotate-180",
                              )}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setOpenGroups((prev) => ({
                                  ...prev,
                                  [item.title]: !prev[item.title],
                                }));
                              }}
                            />
                          </NavLink>
                        </SidebarMenuButton>
                        <CollapsibleContent>
                          <SidebarMenuSub>
                            {filteredItems.map((subItem) => (
                              <SidebarMenuSubItem key={subItem.href}>
                                <SidebarMenuSubButton
                                  asChild
                                  className={cn(
                                    isActive(subItem.href) &&
                                      "bg-sidebar-accent text-primary font-medium",
                                  )}
                                >
                                  <NavLink
                                    to={subItem.href}
                                    onClick={closeMobileSidebar}
                                  >
                                    <subItem.icon className="h-4 w-4" />
                                    {subItem.title}
                                  </NavLink>
                                </SidebarMenuSubButton>
                              </SidebarMenuSubItem>
                            ))}
                            {item.extraSubItems && (
                              <item.extraSubItems
                                onNavigate={closeMobileSidebar}
                              />
                            )}
                          </SidebarMenuSub>
                        </CollapsibleContent>
                      </SidebarMenuItem>
                    </Collapsible>
                  );
                }

                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      className={cn(
                        isActive(item.href!) &&
                          "bg-sidebar-accent text-primary font-medium",
                      )}
                    >
                      <NavLink to={item.href!} onClick={closeMobileSidebar}>
                        <item.icon className="h-4 w-4" />
                        {item.title}
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {role === "admin" && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-xs uppercase tracking-wider text-muted-foreground">
              Admin
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavigation.map((item) =>
                  item.items ? (
                    item.items
                      .filter(
                        (subItem) =>
                          !subItem.devToolsOnly || devToolsStatus.data?.enabled,
                      )
                      .map((subItem) => (
                        <SidebarMenuItem key={subItem.href}>
                          <SidebarMenuButton
                            asChild
                            className={cn(
                              isActive(subItem.href) &&
                                "bg-sidebar-accent text-primary font-medium",
                            )}
                          >
                            <NavLink to={subItem.href}>
                              <subItem.icon className="h-4 w-4" />
                              {subItem.title}
                            </NavLink>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))
                  ) : (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton
                        asChild
                        className={cn(
                          isActive(item.href!) &&
                            "bg-sidebar-accent text-primary font-medium",
                        )}
                      >
                        <NavLink to={item.href!}>
                          <item.icon className="h-4 w-4" />
                          {item.title}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ),
                )}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-primary text-xs">
                {userInitials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{user?.email}</p>
              <p className="text-xs text-muted-foreground capitalize">
                {isEmployee ? "Employee" : "Freelancer"}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon-sm"
            className="text-muted-foreground hover:text-foreground shrink-0"
            onClick={signOut}
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
